import { useMemo, useState } from 'react';
import type { FormProps } from '../components/TaskForm';
import type { CompanyInfo } from '../components/BulkUploadPreview';
import { normalizePageLimitValue } from '../utils/validators';

const MAX_DEPTH = 6;

const defaultFormValues: FormProps = {
  is_include_links: true,
  depth: 3,
  take_screenshot: true,
  url: '',
  hard_parse: false,
  companies_info: [],
  should_create_demo_bot: false,
  page_limit: 250,
};

export const useScrapingForm = () => {
  const [formId, setFormId] = useState(1);
  const [formValues, setFormValues] = useState<FormProps>(defaultFormValues);
  const [bulkUploadData, setBulkUploadData] = useState<CompanyInfo[]>([]);
  const [formKey, setFormKey] = useState(1);

  const handleFormChange = (newValues: FormProps) => {
    setFormValues(prev => ({ ...prev, ...newValues }));
  };

  const handleBulkUpload = (rows: Record<string, string>[] | null) => {
    if (!rows) {
      setBulkUploadData([]);
      return;
    }

    const companies = rows
      .map(row => ({
        company_name: row.company_name,
        url: row.url,
      }))
      .filter(company => company.company_name && company.url);

    setBulkUploadData(companies);
  };

  const handleClearBulkUpload = () => {
    setBulkUploadData([]);
  };

  const resetFormState = () => {
    setFormValues(defaultFormValues);
    setBulkUploadData([]);
    setFormKey(k => k + 1);
  };

  const taskData = useMemo((): FormProps => {
    const depthValue = formValues.depth === MAX_DEPTH ? null : formValues.depth;
    const normalizedPageLimit = normalizePageLimitValue(formValues.page_limit);

    return {
      url: bulkUploadData.length > 0 ? '' : formValues.url,
      depth: depthValue,
      is_include_links: formValues.is_include_links,
      take_screenshot: formValues.take_screenshot,
      hard_parse: formValues.hard_parse,
      organisation_name:
        bulkUploadData.length > 0 ? '' : formValues.organisation_name,
      agent_id: formValues.agent_id,
      include_path_regex: formValues.include_path_regex,
      exclude_path_regex: formValues.exclude_path_regex,
      campaign_name: formValues.campaign_name,
      should_create_demo_bot: formValues.should_create_demo_bot,
      companies_info: bulkUploadData.length > 0 ? bulkUploadData : undefined,
      page_limit: normalizedPageLimit,
    };
  }, [formValues, bulkUploadData]);

  return {
    formId,
    setFormId,
    formKey: String(formKey),
    formValues,
    setFormValues: handleFormChange,
    bulkUploadData,
    handleBulkUpload,
    handleClearBulkUpload,
    resetFormState,
    taskData,
  };
};
